/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { AiEmissionInfo } from "../../models/http/ai/ai-emission-info.model";
import { EnvironmentalImpactByModel } from "../../models/http/ai/ai-environmental-impact-by-model.model";
import { EnvironmentalImpact } from "../../models/http/ai/ai-environmental-impact.model";
import { AiFileStatusResponse } from "../../models/http/ai/ai-file-status-response.model";
import { AiFilesList } from "../../models/http/ai/ai-files-list.model";
import { AiGeneralInformation } from "../../models/http/ai/ai-general-information.model";
import { WaterUsageByModel } from "../../models/http/ai/ai-water-usage-by-model.model";
import { BarChart } from "../../models/http/charts/bar-chart/bar-chart.model";
import { LineChart } from "../../models/http/charts/line-chart/line-chart.model";
import { ChartResponse } from "../../models/ui/charts.model";
import { HttpMethods } from "../../models/ui/http-methods.model";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";
import { isNullOrUndefined } from "../../utils/utils";
import { CommunicationService } from "../communication.service";

@Injectable({
	providedIn: "root"
})
export class DashboardAIHttpService {
	private _dashboardAIOperations = ENDPOINT_GROUPS.ai.operations;

	constructor(private _communicationService: CommunicationService) {}

	public getFiles(tenantId: string): Observable<AiFilesList> {
		const pathParams = {
			tenantId
		};

		// this._communicationService.callService(
		//     this._aiOperations.getFiles.service,
		//     HttpMethods.GET,
		//     { params }
		// ).subscribe((e)=>{console.log(e)})

		return this._communicationService.callService(
			this._dashboardAIOperations.getFiles.service,
			HttpMethods.GET,
			{ pathParams }
		);
	}

	/**
	 * Uploads a file associated with a specific tenant.
	 *
	 * @param tenantId - The unique identifier of the tenant.
	 * @param file - The file to be uploaded, wrapped in a FormData object.
	 * @param isTemporary - Optional flag indicating if the upload is temporary.
	 * @returns An Observable emitting an object containing the uploaded file's ID.
	 */
	public uploadFile(
		tenantId: string,
		file: FormData,
		isTemporary?: boolean
	): Observable<{ fileId: string }> {
		const pathParams = { tenantId };
		const paramsObject: Record<string, string> = {};

		if (!isNullOrUndefined(isTemporary)) {
			paramsObject["isTemporary"] = String(isTemporary);
		}

		const options: { params?: HttpParams; pathParams: typeof pathParams } = {
			pathParams
		};

		if (Object.keys(paramsObject).length) {
			options.params = new HttpParams({ fromObject: paramsObject });
		}

		return this._communicationService.callService(
			this._dashboardAIOperations.uploadFile.service,
			HttpMethods.POST,
			options,
			file
		);
	}

	/**
	 * Retrieves the current processing status of an uploaded file.
	 *
	 * @param tenantId - The unique identifier of the tenant.
	 * @param fileId - The unique identifier of the file.
	 * @returns An observable emitting the file processing status.
	 */
	public getFileStatus(tenantId: string, fileId: string): Observable<AiFileStatusResponse> {
		const pathParams = { tenantId, fileId };

		return this._communicationService.callService(
			this._dashboardAIOperations.getFileStatus.service,
			HttpMethods.GET,
			{ pathParams }
		);
	}

	/**
	 * Retrieves paginated emissions data associated with a specific AI file.
	 *
	 * @param tenantId - The tenant identifier.
	 * @param fileId - The file identifier.
	 * @param page - The page number (0-indexed) to retrieve.
	 * @param size - The number of records per page.
	 * @returns An observable emitting paginated emissions information.
	 */
	public getFileEmissions(
		tenantId: string,
		fileId: string,
		page: number,
		size: number
	): Observable<AiEmissionInfo> {
		const pathParams = { tenantId, fileId };
		const params = new HttpParams({
			fromObject: {
				page: page.toString(),
				size: size.toString()
			}
		});

		return this._communicationService.callService(
			this._dashboardAIOperations.getFileEmissions.service,
			HttpMethods.GET,
			{ pathParams, params }
		);
	}

	/**
	 * Renames an uploaded file.
	 *
	 * @param tenantId - The tenant identifier.
	 * @param fileId - The file identifier.
	 * @param name - The new file name.
	 * @returns An observable emitting when the rename completes.
	 */
	public renameFile(tenantId: string, fileId: string, name: string): Observable<void> {
		const pathParams = { tenantId, fileId };

		return this._communicationService.callService(
			this._dashboardAIOperations.renameFile.service,
			HttpMethods.PUT,
			{ pathParams },
			{ name }
		);
	}

	/**
	 * Deletes an uploaded file.
	 *
	 * @param tenantId - The tenant identifier.
	 * @param fileId - The file identifier.
	 * @returns An observable emitting when the deletion completes.
	 */
	public deleteFile(tenantId: string, fileId: string): Observable<void> {
		const pathParams = { tenantId, fileId };

		return this._communicationService.callService(
			this._dashboardAIOperations.deleteFile.service,
			HttpMethods.DELETE,
			{ pathParams }
		);
	}

	/**
	 * Exports the AI processing data for a file as CSV content.
	 *
	 * @param tenantId - The tenant identifier.
	 * @param fileId - The file identifier.
	 * @returns An observable emitting the CSV payload as a Blob.
	 */
	public exportFile(fileId: string): Observable<Blob> {
		const pathParams = { fileId };

		return this._communicationService.callService(
			this._dashboardAIOperations.exportFile.service,
			HttpMethods.POST,
			{ pathParams, responseType: "blob" }
		);
	}

	public getGeneralInformation(
		tenantId: string,
		fileId: string
	): Observable<AiGeneralInformation> {
		const pathParams = { tenantId, fileId };

		return this._communicationService.callService(
			this._dashboardAIOperations.getGeneralInformation.service,
			HttpMethods.GET,
			{ pathParams }
		);
	}

	public getCo2AppsPerformanceLineChart(
		tenantId: string,
		fileId: string,
		startDate?: string,
		endDate?: string,
		applicationIds: string[] = []
	): Observable<ChartResponse<LineChart>> {
		return this._fetchLineChart(
			this._dashboardAIOperations.getCo2AppsPerformance.service,
			tenantId,
			fileId,
			startDate,
			endDate,
			applicationIds
		);
	}

	public getWaterAppsPerformanceLineChart(
		tenantId: string,
		fileId: string,
		startDate?: string,
		endDate?: string,
		applicationIds: string[] = []
	): Observable<ChartResponse<LineChart>> {
		return this._fetchLineChart(
			this._dashboardAIOperations.getWaterAppsPerformance.service,
			tenantId,
			fileId,
			startDate,
			endDate,
			applicationIds
		);
	}

	public getEnvironmentalImpactByCompany(
		tenantId: string,
		fileId: string,
		companies: string[] = []
	): Observable<EnvironmentalImpact> {
		const pathParams = { tenantId, fileId };
		const body = companies.length ? { companies } : {};

		return this._communicationService.callService(
			this._dashboardAIOperations.getEnvironmentalImpactByCompany.service,
			HttpMethods.POST,
			{ pathParams },
			body
		);
	}

	public getEnvironmentalImpactByModel(
		tenantId: string,
		fileId: string,
		models: string[] = []
	): Observable<EnvironmentalImpactByModel> {
		const pathParams = { tenantId, fileId };
		const body = models.length ? { models } : {};

		return this._communicationService.callService(
			this._dashboardAIOperations.getEnvironmentalImpactByModel.service,
			HttpMethods.POST,
			{ pathParams },
			body
		);
	}

	public getWaterUsageByModel(
		fileId: string,
		models: string[] = []
	): Observable<WaterUsageByModel> {
		const pathParams = { fileId };
		const body = models.length ? { models } : {};

		return this._communicationService.callService(
			this._dashboardAIOperations.getWaterUsageByModel.service,
			HttpMethods.POST,
			{ pathParams },
			body
		);
	}

	private _fetchLineChart(
		operation: string,
		tenantId: string,
		fileId: string,
		startDate?: string,
		endDate?: string,
		applicationIds: string[] = []
	): Observable<ChartResponse<LineChart>> {
		const paramsObject: Record<string, string | string[]> = {};
		if (startDate) paramsObject["startDate"] = startDate;
		if (endDate) paramsObject["endDate"] = endDate;
		if (applicationIds.length) paramsObject["applicationId"] = applicationIds;

		const params = new HttpParams({ fromObject: paramsObject });
		const pathParams = { tenantId, fileId };

		return this._communicationService
			.callService(operation, HttpMethods.GET, { pathParams, params })
			.pipe(
				map((response: LineChart) => this._mapLineChartResponse(response)),
				catchError(() => of({ chart: {} as LineChart, hasError: true }))
			);
	}

	private _mapLineChartResponse(response: LineChart): ChartResponse<LineChart> {
		return { chart: response, hasError: false };
	}

	public getBest10ModelsByEmission(fileId: string): Observable<ChartResponse<BarChart>> {
		const pathParams = { fileId };

		return this._communicationService
			.callService(
				this._dashboardAIOperations.getBest10ModelsByEmission.service,
				HttpMethods.GET,
				{ pathParams }
			)
			.pipe(
				map((response: BarChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	public getBest10ModelsByWaterUsage(fileId: string): Observable<ChartResponse<BarChart>> {
		const pathParams = { fileId };

		return this._communicationService
			.callService(
				this._dashboardAIOperations.getBest10ModelsByWaterUsage.service,
				HttpMethods.GET,
				{ pathParams }
			)
			.pipe(
				map((response: BarChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	public downloadTemplate(): Observable<Blob> {
		return this._communicationService.callService(
			this._dashboardAIOperations.downloadTemplate.service,
			HttpMethods.GET,
			{ responseType: "blob" }
		);
	}
}
